'use client';

import { useEffect, useRef, useState, useId } from 'react';

interface StaticPageRendererProps {
  content: string;
  title?: string;
}

/**
 * Sanitize string to remove invalid characters that cause DOMException.
 * Same logic as WidgetRenderer.tsx HTMLWidget.
 */
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    return str
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/[\x80-\x9F]/g, '')
      .replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    return '';
  }
}

/**
 * StaticPageRenderer — Renders Quintype static page HTML content in an iframe.
 * Uses the same iframe + document.write() pattern as HTMLWidget in WidgetRenderer.tsx.
 * Handles script execution, auto-resizing, and domain rewriting.
 */
export default function StaticPageRenderer({ content, title }: StaticPageRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const writtenRef = useRef(false);
  const [iframeHeight, setIframeHeight] = useState<number>(500);
  const uniqueId = useId().replace(/:/g, '').replace(/^(\d)/, 'sp$1');
  const widgetId = `static-page-${uniqueId}`;

  // Listen for resize messages from iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === 'static-page-resize' &&
        event.data.widgetId === widgetId
      ) {
        const newHeight = Math.ceil(event.data.height);
        setIframeHeight((prev) => (Math.abs(newHeight - prev) > 5 ? newHeight : prev));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [widgetId]);

  // Write HTML into iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || writtenRef.current) return;

    const sanitizedHtml = sanitizeForDOM(content);

    // Rewrite hardcoded dinamani.com domain links in href attributes
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const rewrittenHtml = currentOrigin
      ? sanitizedHtml.replace(
          /href\s*=\s*"(https?:\/\/(?:www\.)?dinamani\.com)([^"]*)"/gi,
          (_, _domain, path) => `href="${currentOrigin}${path}"`
        )
      : sanitizedHtml;

    // Resize script for auto-height
    const resizeScript = `<script>
(function(){
  var _wid = '${widgetId}';
  var _lastH = 0;
  var _timer = null;
  function _sh(){
    if (_timer) return;
    _timer = setTimeout(function(){
      _timer = null;
      try {
        var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        if (Math.abs(h - _lastH) > 5) {
          _lastH = h;
          window.parent.postMessage({type:'static-page-resize',widgetId:_wid,height:h},'*');
        }
      } catch(e){}
    }, 150);
  }
  window.addEventListener('load', _sh);
  window.addEventListener('resize', _sh);
  if(typeof MutationObserver !== 'undefined'){
    new MutationObserver(_sh).observe(document.body,{childList:true,subtree:true,attributes:true});
  }
  setInterval(_sh, 3000);
})();
<\/script>`;

    const headInsert = '<base target="_parent">'
      + '<meta name="viewport" content="width=device-width, initial-scale=1.0">';

    // Detect if content is a full HTML document
    const isFullDocument = /<!DOCTYPE|<html[\s>]/i.test(rewrittenHtml);

    let fullDoc: string;

    if (isFullDocument) {
      let doc = rewrittenHtml;

      if (/<head[^>]*>/i.test(doc)) {
        doc = doc.replace(/<head[^>]*>/i, '$&' + headInsert);
      } else if (/<html[^>]*>/i.test(doc)) {
        doc = doc.replace(/<html[^>]*>/i, '$&<head>' + headInsert + '</head>');
      }

      if (/<\/body>/i.test(doc)) {
        doc = doc.replace(/<\/body>/i, resizeScript + '</body>');
      } else {
        doc += resizeScript;
      }

      fullDoc = doc;
    } else {
      // Fragment: wrap with basic structure
      fullDoc = `<!DOCTYPE html><html><head>${headInsert}</head><body>${rewrittenHtml}${resizeScript}</body></html>`;
    }

    const tryWrite = (): boolean => {
      if (writtenRef.current) return true;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return false;
        doc.open();
        doc.write(fullDoc);
        doc.close();
        writtenRef.current = true;
        return true;
      } catch {
        iframe.srcdoc = fullDoc;
        writtenRef.current = true;
        return true;
      }
    };

    if (!tryWrite()) {
      const onLoad = () => tryWrite();
      iframe.addEventListener('load', onLoad, { once: true });
      const timer = setTimeout(() => tryWrite(), 200);
      return () => {
        iframe.removeEventListener('load', onLoad);
        clearTimeout(timer);
      };
    }
  }, [content, widgetId]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      <iframe
        ref={iframeRef}
        id={widgetId}
        style={{
          width: '100%',
          height: `${iframeHeight}px`,
          border: 'none',
          display: 'block',
          maxWidth: '100%',
        }}
        scrolling="no"
        title={title || 'Static Page'}
      />
    </div>
  );
}
